import React, { useEffect, useState } from 'react';
import { X, Download, BookOpen } from 'lucide-react';
import PwaInstallGuide from './PwaInstallGuide';

const KEY = 'skk_pwa_hint_dismissed_at';
const NEVER = 'skk_pwa_hint_never';
const REPEAT_DAYS = 30;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
const isMobile = () => /iphone|ipad|ipod|android/i.test(window.navigator.userAgent);

export const PwaInstallHint = () => {
  const [visible, setVisible] = useState(false);
  const [promptEvent, setPromptEvent] = useState(null);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(NEVER) === '1') return;
    const dismissed = Number(localStorage.getItem(KEY) || 0);
    if (dismissed && Date.now() - dismissed < REPEAT_DAYS * 86400000) return;
    const onPrompt = (e) => { e.preventDefault(); setPromptEvent(e); setVisible(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => { setVisible(false); setGuide(false); localStorage.setItem(NEVER, '1'); };
    window.addEventListener('appinstalled', onInstalled);
    if (isMobile()) setVisible(true);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);

  if (!visible) return null;

  const later = () => { localStorage.setItem(KEY, String(Date.now())); setVisible(false); setGuide(false); };
  const never = () => { localStorage.setItem(NEVER, '1'); setVisible(false); setGuide(false); };
  const install = async () => {
    if (!promptEvent) { setGuide(true); return; }
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice.catch(() => ({ outcome: 'dismissed' }));
    if (outcome === 'accepted') never(); else later();
  };

  return (
    <>
      <div data-testid="pwa-install-hint" className="mb-4 rounded-2xl bg-white border border-gray-200 shadow-sm p-3 sm:p-3.5 flex items-center gap-3 relative overflow-hidden">
        <span className="absolute inset-y-0 left-0 w-1 bg-brand-gold" aria-hidden="true" />
        <img src="/icons/icon-192.png?v=3" alt="" className="w-12 h-12 rounded-[22%] shadow-md shrink-0 ml-1" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-brand-navy leading-tight">Ihr Kundenportal als App</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">Ein Tipp auf dem Startbildschirm – und Sie sind drin: Vorgänge melden, Dokumente abrufen, Nachrichten lesen. Kostenlos, ohne App Store, in 30 Sekunden eingerichtet.</p>
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={install} data-testid="pwa-install-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-brand-navy text-white text-xs font-semibold hover:bg-brand-navy-light">
              <Download className="w-3.5 h-3.5" /> {promptEvent ? 'Jetzt installieren' : 'So geht\u2019s'}
            </button>
            {promptEvent && (
              <button type="button" onClick={() => setGuide(true)} data-testid="pwa-guide-btn" className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-xs font-medium text-brand-navy hover:bg-brand-navy/5">
                <BookOpen className="w-3.5 h-3.5" /> Anleitung
              </button>
            )}
          </div>
        </div>
        <button type="button" onClick={later} aria-label="Hinweis schließen" data-testid="pwa-install-dismiss"
          className="self-start p-1.5 -mr-1 -mt-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100">
          <X className="w-4 h-4" />
        </button>
      </div>
      {guide && <PwaInstallGuide onClose={() => setGuide(false)} onNever={never} promptEvent={promptEvent} onInstall={install} />}
    </>
  );
};

export default PwaInstallHint;
